/**
 * "Did this fan block that account?" answered from a roster that carries its
 * own completeness (ISSUE-753).
 *
 * This is a deliberate verbatim twin of `apps/mobile/src/moderation/blockedRoster.ts`,
 * kept identical for the same reason `moderationRules.ts` is: there is no
 * shared non-SDK package between the two apps (ISSUE-269 section 5.7).
 * ISSUE-753 is the card that made this a module rather than a plain `Set`
 * built inline on `ConversationPane.tsx`.
 *
 * `packages/profile-sdk/src/private/sdk-helpers/call.ts` never rejects: every
 * transport failure, timeout, or non-2xx becomes an error envelope, not a
 * thrown rejection. So the reachable failure signal is the envelope
 * (`isFailedSdkResult`), and `isError` is read anyway as defense in depth
 * against a future change to `call.ts` or its `apps/app` caller that lets a
 * rejection through, so that change could not silently reopen this hole.
 *
 * A read still pending is the one no-data case that still reads as complete:
 * it has not answered yet, and reading it as incomplete would shield every
 * message in every chat for the first few hundred milliseconds of every open.
 * A failed refetch is different even when a previous page is still in hand,
 * because that page may predate a block the fan has since added, so it stays
 * incomplete.
 *
 * `isOwnMessage` short circuits `isSenderBlockedByRoster` before any of that,
 * because `postBlockAddHandler` refuses a self block with `VALIDATION_ERROR`
 * and `reason: "cannotBlockSelf"`, so a fan's own message is provably never
 * from a blocked sender and no roster read is needed to know it. Without that
 * carve out, making the fail closed branch reachable would also shield a
 * fan's own messages behind "Show" for the life of a mounted screen whenever
 * block/list errors, since this screen sets no `refetchInterval`.
 */

import { isBlockedSender, isFailedSdkResult } from "./moderationRules";

/**
 * How many blocked rows the conversation pane asks `block/list` for.
 *
 * `block/list` became cursor paged in ISSUE-376 and answers with 50 rows when
 * the caller sends no limit (`BLOCK_LIST_DEFAULT_LIMIT`,
 * `services/profile-api/src/repo/blocks.ts`). The handler clamps whatever it is
 * given down to `BLOCK_LIST_MAX` (`postBlockListHandler/index.ts:70`), and
 * `postBlockAddHandler/index.ts:65` refuses a new block once the fan already
 * holds `BLOCK_LIST_MAX` (`services/profile-api/src/models/blocks.ts:12`, value
 * 500). The cap on how many blocks can exist and the ceiling on one page are
 * therefore the same number, which makes a page this size the whole roster by
 * construction. That equality is an invariant this module's own test checks, so
 * raising the server cap reddens the build here rather than silently truncating
 * a fan's roster again.
 */
export const BLOCKED_ROSTER_PAGE_LIMIT = 500;

export type typeBlockedRoster = {
  blockedAccountIds: ReadonlySet<string>;
  /** false when block/list returned a nextCursor, meaning this page is not the whole roster. */
  isComplete: boolean;
};

/**
 * The whole `useBlockList` query state, not just its settled page. `response`
 * is declared as a REQUIRED property whose type includes `undefined`, rather
 * than an optional property, because `exactOptionalPropertyTypes` refuses an
 * optional property being handed an explicit `undefined`, and the call site
 * passes `blockList.data`, which is typed `... | undefined`.
 */
export type typeBlockListReadState = {
  isPending: boolean;
  isError: boolean;
  response:
    | {
        error?: { code: string } | null | undefined;
        data?:
          | {
              items?: readonly { accountId: string }[] | null | undefined;
              nextCursor?: string | null | undefined;
            }
          | null
          | undefined;
      }
    | null
    | undefined;
};

/**
 * Builds the roster from the whole `block/list` query state, so a read that
 * failed can never be classified as a complete roster. The parameter is
 * structural rather than the generated response type, so this module needs
 * no SDK import; the call site passes `blockList.isPending`, `blockList.isError`
 * and `blockList.data`, whose fields match.
 *
 * The order below is load bearing: pending beats error, error beats a failed
 * envelope, and only a settled, non failed read reaches the page's own
 * `nextCursor`.
 */
export function toBlockedRoster(
  input: typeBlockListReadState,
): typeBlockedRoster {
  const blockedAccountIds = new Set(
    (input.response?.data?.items ?? []).map((entry) => entry.accountId),
  );
  if (input.isPending) {
    return { blockedAccountIds, isComplete: true };
  }
  if (input.isError) {
    return { blockedAccountIds, isComplete: false };
  }
  if (isFailedSdkResult(input.response ?? {})) {
    return { blockedAccountIds, isComplete: false };
  }
  return {
    blockedAccountIds,
    isComplete: (input.response?.data?.nextCursor ?? null) === null,
  };
}

/**
 * The membership test behind the conversation pane's blocked-message
 * treatment, and it fails closed.
 *
 * A fan's own message is never shielded, checked first and unconditionally:
 * `postBlockAddHandler` refuses a self block, so no roster read can ever make
 * this answer anything but `false`, including an unreadable roster.
 *
 * A sender present in the roster is blocked. A sender absent from an INCOMPLETE
 * roster is unknown rather than safe, and unknown resolves to blocked. The
 * asymmetry decides it: over shielding costs the fan one tap on a Show
 * control, while under shielding shows a fan content from somebody they
 * blocked, on a store compliance surface (Apple 1.2, Google UGC).
 *
 * At today's server values the truncation half of this branch is unreachable,
 * because `BLOCKED_ROSTER_PAGE_LIMIT` equals `BLOCK_LIST_MAX` so `nextCursor`
 * is always null on a settled read. The failed-read half is reachable any time
 * `block/list` errors. It is built anyway: it is the graceful half of a pair
 * whose other half is a red build, and a cap raise reaches the server long
 * before a client update reaches a browser.
 */
export function isSenderBlockedByRoster(input: {
  senderId: string;
  isOwnMessage: boolean;
  roster: typeBlockedRoster;
}): boolean {
  if (input.isOwnMessage) return false;
  if (
    isBlockedSender({
      senderId: input.senderId,
      blockedAccountIds: input.roster.blockedAccountIds,
    })
  ) {
    return true;
  }
  return !input.roster.isComplete;
}
